// Colour and number formatting, in one place.
//
// The palette is the validated default from the `dataviz` skill, not hand-picked.
// Two sets are in use and each was run through scripts/validate_palette.js
// against this app's white surface before being written down:
//
//   stores (categorical, 3 slots, --pairs all)
//     PASS lightness band, chroma floor, CVD separation (worst ΔE 9.2),
//     normal-vision floor (worst ΔE 24.0). Aqua warns below 3:1 contrast, which
//     obliges visible labels — the store bar is directly labelled and the same
//     numbers appear in the drill-down table, so the relief rule is satisfied.
//
//   aging (ordinal ramp, 5 steps, --ordinal)
//     PASS monotone lightness, adjacent ΔL, light-end contrast (2.11:1),
//     single hue (3° spread).
//
// Do not add a sixth aging step or a fourth store hue by eye. Re-run the
// validator — a generated hue is indistinguishable from an existing one under
// colour-vision deficiency and breaks every check silently.

export const palette = {
  // Ordered scale: older debt is darker. Opening balances sit OUTSIDE this ramp
  // in muted grey on purpose — they are not "older", they are "not aged".
  aging: ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281'],
  agingLabels: ['0–30 days', '31–60 days', '61–90 days', '91–120 days', 'Over 120 days'],
  opening: '#898781',
  advance: '#1baf7a',

  // Categorical slots 1–3, in the skill's fixed order. Never cycled.
  stores: ['#2a78d6', '#eb6834', '#1baf7a'],

  series: '#2a78d6',
  seriesInProgress: '#86b6ef',

  // Status colours are reserved and never reused as a series colour. Each one
  // ships with a word beside it, never colour alone.
  good: '#0ca30c',
  warning: '#fab219',
  critical: '#d03b3b',

  ink: '#0b0b0b',
  inkSecondary: '#52514e',
  inkMuted: '#898781',
  gridline: '#e1e0d9',
  baseline: '#c3c2b7',
  surface: '#ffffff',
  plane: '#f7f7f5',
};

const N = (v) => Number(v ?? 0);

/** Rs 15,287,204.63 — full precision, for tables and tooltips. */
export const money = (v) =>
  `Rs ${N(v).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Rs 116.1M — for tile values, where the point is to be read at a glance.
 * Rs 116,116,100.95 on a tile is a string of digits the eye has to count; the
 * exact figure is one hover or one click away and never lost.
 */
export const compact = (v) => {
  const n = N(v);
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e9) return `${sign}Rs ${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}Rs ${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}Rs ${(a / 1e3).toFixed(0)}K`;
  return `${sign}Rs ${a.toFixed(0)}`;
};

export const qty = (v) =>
  N(v).toLocaleString('en-LK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** 2026-04-10 → 10 Apr 2026. Never render a bare ISO date at a person. */
export const prettyDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-LK', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** 2026-07 → Jul 2026 */
export const prettyMonth = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-LK', { month: 'short', year: 'numeric' });
};
